import React, { useState, useMemo } from "react";
import {
  FileText,
  Building2,
  Search,
  CheckCircle2,
  AlertCircle,
  Users,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Filter,
  X,
  Network,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { Check, ChevronsUpDown } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

const STATUS_CONFIG = {
  complete: {
    label: "Completo",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    icon: CheckCircle2,
  },
  partial: {
    label: "Parcial",
    color: "bg-amber-50 text-amber-700 border-amber-200",
    icon: AlertCircle,
  },
  pending: {
    label: "Pendente",
    color: "bg-slate-50 text-slate-500 border-slate-200",
    icon: AlertCircle,
  },
} as const;

const PAGE_SIZE = 20;

export default function Documents() {
  usePageTitle("Documentos");
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Filter state
  const [search, setSearch] = useState("");
  const [selectedChannelId, setSelectedChannelId] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [page, setPage] = useState(1);

  // Debounced search — reset page on filter change
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    setDebounceTimer(timer);
  };

  const handleChannelChange = (value: string) => {
    setSelectedChannelId(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setSelectedChannelId("all");
    setSelectedStatus("all");
    setPage(1);
  };

  const isAdmin = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const isClientScopedUser = user?.role === "client_rh";
  const showFilters = !isClientScopedUser;
  const hasActiveFilters =
    showFilters && (
      debouncedSearch !== "" ||
      selectedChannelId !== "all" ||
      selectedStatus !== "all"
    );

  // Query input — memoized to prevent infinite re-renders
  const queryInput = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      channelId:
        isAdmin && selectedChannelId !== "all"
          ? Number(selectedChannelId)
          : undefined,
      status:
        showFilters && selectedStatus !== "all"
          ? (selectedStatus as "complete" | "partial" | "pending")
          : undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, isAdmin, selectedChannelId, selectedStatus, page, showFilters]
  );

  // Fetch companies (paginated)
  const {
    data: result,
    isLoading,
    isError,
    refetch,
  } = trpc.documents.listCompanies.useQuery(queryInput, {
    retry: 1,
  });

  // Fetch channels for filter (perfis administrativos globais)
  const { data: channels } = trpc.documents.listChannelsForFilter.useQuery(
    undefined,
    {
      enabled: isAdmin,
      retry: 1,
      staleTime: 60_000,
    }
  );

  // Show error toast
  if (isError && !isLoading) {
    toast.error("Erro ao carregar empresas. Tente novamente.", {
      id: "documents-error",
    });
  }

  const companies = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  const totalPages = result?.totalPages ?? 1;

  const formatCnpj = (cnpj: string | null) => {
    if (!cnpj) return "—";
    const clean = cnpj.replace(/\D/g, "");
    if (clean.length !== 14) return cnpj;
    return clean.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  return (
    <SalviaLayout title="Documentos">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-teal-100 rounded-lg">
              <FileText className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Documentos — Contexto por Empresa
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {isClientScopedUser
                  ? "Aqui você visualiza o contexto documental das empresas disponíveis no seu escopo hierárquico."
                  : "Selecione uma empresa para configurar dados cadastrais, estrutura organizacional, solicitante e avaliadores."}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters ? (
          <div className="mb-6 bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filtros</span>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="ml-auto text-xs text-slate-500 hover:text-slate-700 h-7 px-2"
                >
                  <X className="w-3 h-3 mr-1" />
                  Limpar filtros
                </Button>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {/* Canal filter — admin only (V31: searchable combobox) */}
              {isAdmin && (
                <ChannelCombobox
                  channels={channels ?? []}
                  value={selectedChannelId}
                  onChange={handleChannelChange}
                />
              )}

              {/* Search */}
              <div className="flex-1 min-w-0">
                <label className="text-xs text-slate-500 mb-1 block">
                  Buscar empresa
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Nome, razão social ou CNPJ..."
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10 h-9"
                  />
                </div>
              </div>

              {/* Status filter */}
              <div className="w-full sm:w-44">
                <label className="text-xs text-slate-500 mb-1 block">
                  Status do contexto
                </label>
                <Select
                  value={selectedStatus}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="complete">Completo</SelectItem>
                    <SelectItem value="partial">Parcial</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 rounded-lg border border-teal-100 bg-teal-50/70 p-4">
            <p className="text-sm font-medium text-slate-700">Empresas do seu escopo hierárquico</p>
            <p className="mt-1 text-sm text-slate-600">
              Esta tela apresenta os contextos documentais das empresas disponíveis no seu escopo, sem alterar permissões operacionais.
            </p>
          </div>
        )}

        {/* Results count */}
        {!isLoading && !isError && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">
              {isClientScopedUser
                ? totalCount === 0
                  ? "Nenhum contexto disponível para as empresas do seu escopo"
                  : totalCount === 1
                  ? "1 empresa disponível no seu escopo"
                  : `${totalCount} empresas disponíveis no seu escopo`
                : totalCount === 0
                ? "Nenhuma empresa encontrada"
                : totalCount === 1
                ? "1 empresa encontrada"
                : `${totalCount} empresas encontradas`}
              {hasActiveFilters && " (filtrado)"}
            </p>
            {totalPages > 1 && (
              <p className="text-sm text-slate-400">
                Página {page} de {totalPages}
              </p>
            )}
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded-lg p-5 animate-pulse"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-48 mb-2" />
                    <div className="h-3 bg-slate-100 rounded w-32" />
                  </div>
                  <div className="h-6 bg-slate-100 rounded w-20" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <div className="text-center py-20">
            <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">
              Erro ao carregar empresas
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              Não foi possível carregar a lista. Verifique sua conexão e tente
              novamente.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && companies.length === 0 && (
          <div className="text-center py-20">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-1">
              Nenhuma empresa encontrada
            </h3>
            <p className="text-sm text-slate-400">
              {isClientScopedUser
                ? "Não há contexto documental disponível para as empresas do seu escopo."
                : hasActiveFilters
                ? "Tente ajustar os filtros ou termos de busca."
                : "Cadastre empresas na seção Clientes para começar."}
            </p>
            {showFilters && hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="mt-3"
              >
                Limpar filtros
              </Button>
            )}
          </div>
        )}

        {/* Companies list */}
        {!isLoading && !isError && companies.length > 0 && (
          <>
            <div className="space-y-3">
              {companies.map((company: any) => {
                const status =
                  STATUS_CONFIG[
                    company.contextStatus as keyof typeof STATUS_CONFIG
                  ] ?? STATUS_CONFIG.pending;
                const StatusIcon = status.icon;

                return (
                  <div
                    key={company.id}
                    className="bg-white border border-slate-200 rounded-lg p-5 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer group"
                    onClick={() => setLocation(`/documents/${company.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="p-2 bg-slate-100 rounded-lg group-hover:bg-teal-50 transition-colors flex-shrink-0">
                          <Building2 className="w-5 h-5 text-slate-500 group-hover:text-teal-600 transition-colors" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 truncate">
                              {company.name}
                            </h3>
                            {company.channelName && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-blue-50 text-blue-600 border-blue-200 flex-shrink-0"
                              >
                                {company.channelName}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            {company.razaoSocial && (
                              <span className="truncate max-w-[200px]">
                                {company.razaoSocial}
                              </span>
                            )}
                            <span>{formatCnpj(company.cnpj)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                        {/* Counters */}
                        <div className="hidden md:flex items-center gap-3 text-xs text-slate-500">
                          <span
                            className="flex items-center gap-1"
                            title="Cargos importados"
                          >
                            <Users className="w-3.5 h-3.5" />
                            {company.importJobsCount} cargos
                          </span>
                          <span title="Avaliadores">
                            {company.evaluatorsCount} avaliador
                            {company.evaluatorsCount !== 1 ? "es" : ""}
                          </span>
                        </div>

                        {/* Status badge */}
                        <Badge
                          variant="outline"
                          className={`text-xs ${status.color} flex-shrink-0`}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>

                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 transition-colors" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 7) {
                      pageNum = i + 1;
                    } else if (page <= 4) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 3) {
                      pageNum = totalPages - 6 + i;
                    } else {
                      pageNum = page - 3 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 p-0 text-xs ${
                          page === pageNum
                            ? "bg-teal-600 hover:bg-teal-700 text-white"
                            : ""
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="gap-1"
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </SalviaLayout>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * V31 — ChannelCombobox: dropdown pesquisável para filtro de Canal
 * ────────────────────────────────────────────────────────────────────────── */

function removeAccents(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function ChannelCombobox({
  channels,
  value,
  onChange,
}: {
  channels: { id: number; name: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = React.useState(false);

  const selectedLabel = React.useMemo(() => {
    if (value === "all") return "Todos os canais";
    const ch = channels.find((c) => String(c.id) === value);
    return ch?.name ?? "Selecionar canal";
  }, [value, channels]);

  return (
    <div className="w-full sm:w-56">
      <label className="text-xs text-slate-500 mb-1 block">Canal</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-9 justify-between font-normal text-sm px-3"
          >
            <div className="flex items-center gap-2 truncate">
              <Network className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="truncate">{selectedLabel}</span>
            </div>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command
            filter={(itemValue, search) => {
              // Case-insensitive + accent-insensitive matching
              const normalizedSearch = removeAccents(search.toLowerCase());
              const normalizedItem = removeAccents(itemValue.toLowerCase());
              return normalizedItem.includes(normalizedSearch) ? 1 : 0;
            }}
          >
            <CommandInput placeholder="Digite para buscar..." />
            <CommandList>
              <CommandEmpty>Nenhum canal encontrado</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__todos_os_canais__"
                  onSelect={() => {
                    onChange("all");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value === "all" ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  Todos os canais
                </CommandItem>
                {channels.map((ch) => (
                  <CommandItem
                    key={ch.id}
                    value={ch.name}
                    onSelect={() => {
                      onChange(String(ch.id));
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        value === String(ch.id) ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    {ch.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
